import type { CreateUserRequest, UpdateUserRequest, UserSummary } from '../../api/types.ts';

export interface UserFormState {
  email: string;
  password: string;
  display_name: string;
  role_id: number | '';
  is_active: boolean;
}

export function initialUserForm(user: UserSummary | null): UserFormState {
  return {
    email: user?.email ?? '',
    password: '',
    display_name: user?.display_name ?? '',
    role_id: user?.role?.id ?? '',
    is_active: user?.is_active ?? true,
  };
}

/**
 * Returns the first validation problem, or null when the form is
 * submittable. `roleRequired` is false when the role picker could not be
 * resolved (a users:manage-only session lacking roles:manage) -- an
 * unresolved role_id must not block editing fields other than the role.
 */
export function validateUserForm(form: UserFormState, isEdit: boolean, roleRequired: boolean): string | null {
  if (form.email.trim() === '' || !form.email.includes('@')) return 'Enter a valid email address.';
  if (!isEdit && form.password.trim() === '') return 'Enter a password.';
  if (roleRequired && form.role_id === '') return 'Select a role.';
  return null;
}

export function buildCreateUserPayload(form: UserFormState): CreateUserRequest {
  const payload: CreateUserRequest = {
    email: form.email.trim(),
    password: form.password,
    role_id: Number(form.role_id),
  };
  const displayName = form.display_name.trim();
  if (displayName !== '') payload.display_name = displayName;
  return payload;
}

/**
 * Only the fields that actually changed go on the wire. Self-edits never
 * include role_id/is_active -- the server 403s both, and the form disables
 * those controls for a self row, so this is belt and braces.
 */
export function buildUpdateUserPatch(form: UserFormState, user: UserSummary, isSelf: boolean): UpdateUserRequest {
  const patch: UpdateUserRequest = {};

  const email = form.email.trim();
  if (email !== user.email) patch.email = email;

  // The API has no clear-display-name affordance (the field is optional but
  // NOT nullable -- the server's zod schema is min(1) and 400s a blank
  // string as a generic validation_error), so a blanked-out field is simply
  // omitted from the patch rather than sent as ''.
  const displayName = form.display_name.trim();
  if (displayName !== '' && displayName !== (user.display_name ?? '')) patch.display_name = displayName;

  if (!isSelf && form.role_id !== '' && Number(form.role_id) !== (user.role?.id ?? null)) {
    patch.role_id = Number(form.role_id);
  }
  if (!isSelf && form.is_active !== user.is_active) patch.is_active = form.is_active;
  if (form.password.trim() !== '') patch.password = form.password;

  return patch;
}
